import React from 'react';
import { TFunction } from 'react-i18next';
import { Formik } from 'formik';
import { withInject, Action } from '@piwikpro/platform';
import {
  Stack,
  Text,
  withTranslation,
  Spinner,
  HelpLink,
} from '@piwikpro/ui-components';
import { AuthSettings } from '@piwikpro/auth-crate';
import { FormValidator } from '@piwikpro/form-crate';
import { Session } from '@piwikpro/auth-crate/Session';
import FailMessage from '../FailMessage';

export interface SessionLengthProps {
  sessionLength: number
  isLoading: boolean
  failed: boolean
  authSettings: AuthSettings
  session: Session
  validation: FormValidator
  t: TFunction
}
const fieldName = 'inactivityTimeout';

interface TextNumberHandlerValue {
  [fieldName]: number
}

const SessionLength: React.FunctionComponent<SessionLengthProps> = ({
  sessionLength,
  session,
  isLoading,
  failed,
  authSettings,
  validation,
  t,
}) => {
  const handleSubmit = async (
    values: TextNumberHandlerValue,
    { setSubmitting, setFieldValue }: Formik,
  ) => {
    try {
      await authSettings.setSessionLength({
        length: values[fieldName],
        successMessage: t('platform.accountSettings.sessionLength.successChange.notification.text', { value: values[fieldName] }),
      });
      await authSettings.getSettingsForOrganization();
      session.getSession();
    } catch (err) {
      setFieldValue(fieldName, sessionLength);
    }
    setSubmitting(false);
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (failed) {
    return <FailMessage />;
  }
  return (
    <Stack vertical horizontalAlignment="middle">
      <Stack.Item fill>
        <Stack vertical spacing="narrow">
          <Stack.Item>
            <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('platform.accountSettings.sessionLength.header')}</Text>
          </Stack.Item>
          <Stack.Item>
            <Text as="p">
              {t('platform.accountSettings.sessionLength.description')}
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item fill>
        <Action.DelayedAutoSavedNumberControl
          id="idle-session-length"
          initialValue={sessionLength}
          validationSchema={validation.createValidationSchema(validators => ({
            [fieldName]: validators.auth.inactivityTimeout,
          }))}
          label={t('platform.accountSettings.sessionLength.input.label')}
          info={t('platform.accountSettings.sessionLength.input.info')}
          name={fieldName}
          type="number"
          units="min"
          onSubmit={handleSubmit}
          required
        />
      </Stack.Item>
      <Stack.Item>
        <HelpLink href="https://help.piwik.pro/support/account/change-the-session-timeout-for-users/">
          Change the session timeout for users
        </HelpLink>
      </Stack.Item>
    </Stack>
  );
};

export default withInject({
  session: 'AuthCrate.session',
  authSettings: 'AuthCrate.authSettings',
  validation: 'FormCrate.validation',
})(withTranslation(['administration'])(SessionLength));
